// What a device can do and how it is set up. The resident agent detects the
// capabilities and sends them whole (`capabilities.put`); the core only stores
// and shows them, so adding a model never needs a core release. The settings
// are written by the person on screen and pushed to the agent (`settings`).
import { z } from "zod";
import { signedShape } from "./instruction";

/** CLI agents the resident process can run. */
export const AGENT_KINDS = ["claude", "codex"] as const;
export const AgentKind = z.enum(AGENT_KINDS);
export type AgentKind = z.infer<typeof AgentKind>;

/** Product names, used on screen and in refusals. */
export const AGENT_KIND_LABEL: Record<AgentKind, string> = {
  claude: "Claude Code",
  codex: "Codex",
};

/** How many recent working directories a device remembers. */
export const RECENT_CWDS_MAX = 8;
/** Immediate subfolders reported per allowed working directory. */
export const WORKDIR_CHILDREN_MAX = 100;
/** Configured working directories that may be reported missing. */
export const MISSING_CWDS_MAX = RECENT_CWDS_MAX + 1;

/**
 * One reasoning effort a model accepts. `id` goes to the CLI as is
 * (codex `-c model_reasoning_effort=<id>`, claude `--effort <id>`).
 */
export const EffortOption = z.object({
  id: z.string().min(1).max(100),
  label: z.string().min(1).max(100),
});
export type EffortOption = z.infer<typeof EffortOption>;

/** One model the device offers. `id` goes to the CLI as is. */
export const ModelOption = z.object({
  id: z.string().min(1).max(200),
  label: z.string().min(1).max(100),
  /** Efforts the model accepts. Empty means effort cannot be chosen. */
  efforts: z.array(EffortOption).max(12),
  /** One of `efforts`, the default of a new chat. null leaves it to the CLI. */
  defaultEffort: z.string().max(100).nullable(),
});
export type ModelOption = z.infer<typeof ModelOption>;

/** One check. When `ok` is false, `reason` and `hint` are shown as is. */
export const Check = z.object({
  ok: z.boolean(),
  reason: z.string().max(500).nullable(),
  hint: z.string().max(500).nullable(),
});
export type Check = z.infer<typeof Check>;

export const Checks = z.object({
  /** When the free checks ran (ISO). */
  at: z.string(),
  /** CLI flags the agent relies on that this CLI version lacks. */
  flags: Check.extend({ missing: z.array(z.string().max(100)).max(50) }),
  /** codex only: MCP servers registered by hand that would clash. */
  foreignMcp: Check.extend({ servers: z.array(z.string().max(100)).max(50) }),
  /** The paid round trip. null until it ran once. */
  live: Check.extend({
    at: z.string(),
    version: z.string().max(200),
  }).nullable(),
});
export type Checks = z.infer<typeof Checks>;

export const WorkdirMarker = z.enum([
  "agents_md",
  "claude_md",
  "skills",
  "git",
]);
export type WorkdirMarker = z.infer<typeof WorkdirMarker>;

export const WorkdirChild = z.object({
  name: z.string().min(1).max(255),
  markers: z.array(WorkdirMarker).max(4),
});
export type WorkdirChild = z.infer<typeof WorkdirChild>;

/** A working directory root the device allows. `path` is its id on the device. */
export const Workdir = z.object({
  path: z.string().min(1).max(1000).startsWith("/"),
  name: z.string().min(1).max(200),
  children: z.array(WorkdirChild).max(WORKDIR_CHILDREN_MAX),
});
export type Workdir = z.infer<typeof Workdir>;

/** One usage-limit window as the subscription endpoint reports it. */
export const RateLimitWindow = z.object({
  /** session = the short (5 hour) window, weekly = the week, model = per-model week. */
  kind: z.enum(["session", "weekly", "model"]),
  model: z.string().max(100).nullable(),
  usedPercent: z.number().min(0).max(100),
  windowSeconds: z.number().int().positive(),
  resetsAt: z.string().nullable(),
});
export type RateLimitWindow = z.infer<typeof RateLimitWindow>;

/**
 * Usage limits fetched periodically. On failure the previous windows stay and
 * `error` says why (`at` is the last success).
 */
export const RateLimits = z.object({
  at: z.string(),
  windows: z.array(RateLimitWindow).max(20),
  error: z.string().max(500).nullable(),
});
export type RateLimits = z.infer<typeof RateLimits>;

/**
 * Whether the CLI is logged in, as the CLI itself says. `unknown` means it
 * could not tell; it does not mean logged out.
 */
export const Auth = z.object({
  at: z.string(),
  state: z.enum(["ok", "loggedOut", "unknown"]),
  account: z.string().max(200).nullable(),
  detail: z.string().max(500).nullable(),
});
export type Auth = z.infer<typeof Auth>;

/**
 * Progress of a login started from the screen. Back to null on success or
 * cancel; `failed` stays until the next start or cancel.
 */
export const Login = z.object({
  requestId: z.string().min(1).max(100),
  state: z.enum(["waiting", "verifying", "failed"]),
  url: z.string().max(4000).nullable(),
  /** Codex's one-time code. null for Claude. */
  userCode: z.string().max(50).nullable(),
  /** Whether a code must be pasted back (Claude). */
  needsCode: z.boolean(),
  expiresAt: z.string(),
  error: z.string().max(500).nullable(),
});
export type Login = z.infer<typeof Login>;

/** One external MCP tool. The flags come from MCP annotations. */
export const ExternalTool = z.object({
  name: z.string().min(1).max(200),
  readOnly: z.boolean(),
  destructive: z.boolean(),
});
export type ExternalTool = z.infer<typeof ExternalTool>;

/**
 * One external MCP server the CLI sees. `appId` is a Codex app id (null for
 * Claude and hand-added servers). `closed` servers are visible but cannot be
 * opened; their tools are empty and a run forbids the whole server.
 */
export const ExternalServer = z.object({
  name: z.string().min(1).max(200),
  appId: z.string().min(1).max(200).nullable(),
  tools: z.array(ExternalTool).max(300),
  closed: z.boolean().optional(),
});
export type ExternalServer = z.infer<typeof ExternalServer>;

export const ExternalTools = z.object({
  seenAt: z.string(),
  servers: z.array(ExternalServer).max(50),
});
export type ExternalTools = z.infer<typeof ExternalTools>;

/** One installed CLI. */
export const AgentCapability = z.object({
  kind: AgentKind,
  label: z.string().min(1).max(100),
  /** The `--version` line (shown, the only clue for an old CLI). */
  version: z.string().max(200),
  models: z.array(ModelOption).max(50),
  defaultModel: z.string().max(200).nullable(),
  /** null until the free checks ran in this process. */
  checks: Checks.nullable(),
  /** null until fetched, or when the CLI has no limits endpoint. */
  rateLimits: RateLimits.nullable(),
  /** null until checked. */
  auth: Auth.nullable(),
  /** A login started from the screen, null when none is running. */
  login: Login.nullable(),
  /** External tools the CLI sees, null until looked up. */
  externalTools: ExternalTools.nullable(),
  /** Whether the agent can place a draft message in Slack for this CLI. */
  slackDraft: z.boolean(),
});
export type AgentCapability = z.infer<typeof AgentCapability>;

/** Everything the device reports, replaced whole by `capabilities.put`. */
export const Capabilities = z.object({
  agents: z.array(AgentCapability).max(10),
  workdirs: z.array(Workdir).max(50),
  /** Configured working directories (settings cwd and recent ones) that do not exist. */
  missingCwds: z.array(z.string().max(1000)).max(MISSING_CWDS_MAX),
  /**
   * Whose instructions this device's agent takes: its own PC's (`self`) and
   * the members of its list of trusted devices that may instruct it
   * (`takes`, empty without a list). Absent: an agent without a list, which takes
   * instructions from anywhere. What a screen shows; the agent checks itself.
   */
  trust: z
    .object({
      /** This PC's own member: its own screen instructs it always. */
      self: z.string().max(100).nullable().optional(),
      /**
       * This device's instruct key (public), when it is a member that only
       * takes instructions (a cloud device): what the person's app adds to
       * the list.
       */
      key: z.string().max(200).nullable().optional(),
      /** The list such a member holds (null: none yet); it holds one only. */
      list: z.string().max(100).nullable().optional(),
      takes: z.array(z.string().max(100)).max(200),
      /** The person turned other devices off: only `self` instructs it. */
      selfOnly: z.literal(true).optional(),
    })
    .nullable()
    .optional(),
});
export type Capabilities = z.infer<typeof Capabilities>;

export const CAPABILITIES_EMPTY: Capabilities = {
  agents: [],
  workdirs: [],
  missingCwds: [],
};

/**
 * How much of the person's other MCP servers a run may use: none, read
 * (read-only tools), or all.
 */
export const OTHER_MCP_MODES = ["none", "read", "all"] as const;
export const OtherMcpMode = z.enum(OTHER_MCP_MODES);
export type OtherMcpMode = z.infer<typeof OtherMcpMode>;

/** Compaction threshold of chats in percent of the window. 100 means never. */
export const ChatCompactPercent = z.number().int().min(30).max(100);

/** Per-device defaults and behaviour of the chat path. */
export const DeviceSettings = z.object({
  /** Default CLI of a new chat (a chat copies it when created). */
  agent: AgentKind,
  /** Default model. null leaves it to the CLI. */
  model: z.string().max(200).nullable(),
  /** Default working directory of a new chat. null means none (no file access). */
  cwd: z.string().max(1000).nullable(),
  recentCwds: z.array(z.string().max(1000)).max(RECENT_CWDS_MAX),
  otherMcp: OtherMcpMode,
  /** Quiet period before a burst of posts starts a run (ms). */
  quietMs: z.number().int().min(500).max(60_000),
  /**
   * parallel: chats with a working directory run at the same time.
   * serial: one such chat at a time across the device.
   */
  concurrency: z.enum(["parallel", "serial"]),
  /** Compact after a reply when the context reaches this share. null or 100 means never. */
  compactPercent: z.object({
    claude: ChatCompactPercent.nullable(),
    codex: ChatCompactPercent.nullable(),
  }),
  /**
   * Whether the CLIs' usage limits are fetched on this device (Claude Code's
   * sign-in to Anthropic, Codex's app-server). false: nothing is fetched and
   * no numbers are shown.
   */
  usageLimits: z.boolean(),
});
export type DeviceSettings = z.infer<typeof DeviceSettings>;

export const DEVICE_SETTINGS_DEFAULT: DeviceSettings = {
  agent: "claude",
  model: null,
  cwd: null,
  recentCwds: [],
  otherMcp: "none",
  quietMs: 1500,
  concurrency: "parallel",
  compactPercent: { claude: 100, codex: 100 },
  usageLimits: true,
};

/** A device as the agent sees it. */
export const Device = z.object({
  id: z.string().min(1).max(100),
  name: z.string().min(1).max(100),
  /** this_pc: the offline edition's own PC. remote: a device of a cloud account. */
  kind: z.enum(["this_pc", "remote"]),
  settings: DeviceSettings,
  settingsRevision: z.number().int().min(0),
  /** The `device_settings` instruction the settings were last written with. */
  settingsSigned: signedShape.nullable().optional(),
});
export type Device = z.infer<typeof Device>;

/** A login instruction from the screen. */
export const LoginCommand = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("start"),
    kind: AgentKind,
    requestId: z.string().min(1).max(100),
  }),
  z.object({
    action: z.literal("code"),
    kind: AgentKind,
    requestId: z.string().min(1).max(100),
    // One line on stdin; a newline would feed a second line to the CLI.
    code: z
      .string()
      .trim()
      .min(1)
      .max(2000)
      .regex(/^[^\r\n]+$/),
  }),
  z.object({
    action: z.literal("cancel"),
    kind: AgentKind,
    requestId: z.string().min(1).max(100),
  }),
]);
export type LoginCommand = z.infer<typeof LoginCommand>;

/**
 * Extracts `x.y.z[-pre]` from a `--version` line or an npm version. null when
 * unreadable.
 */
export function parseCliVersion(
  raw: string,
): { nums: [number, number, number]; pre: string | null } | null {
  const hit = /(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?/.exec(raw);
  if (!hit) return null;
  return {
    nums: [Number(hit[1]), Number(hit[2]), Number(hit[3])],
    pre: hit[4] ?? null,
  };
}

/**
 * Whether version `a` is newer than `b`. null when either is unreadable. A
 * prerelease is older than the release with the same numbers.
 */
export function isNewerCliVersion(a: string, b: string): boolean | null {
  const x = parseCliVersion(a);
  const y = parseCliVersion(b);
  if (!x || !y) return null;
  for (let i = 0; i < 3; i++)
    if (x.nums[i] !== y.nums[i]) return x.nums[i] > y.nums[i];
  if (x.pre === y.pre) return false;
  if (x.pre === null) return true;
  if (y.pre === null) return false;
  return x.pre.localeCompare(y.pre, "en", { numeric: true }) > 0;
}

/** Absolute and normalized (no `.`, `..`, empty segments, no trailing `/`); `/` itself is not. */
export function isCanonicalAbsPath(p: string): boolean {
  if (p.length === 0 || p.length > 1000 || !p.startsWith("/") || p === "/")
    return false;
  if (p.endsWith("/")) return false;
  return p
    .slice(1)
    .split("/")
    .every((seg) => seg !== "" && seg !== "." && seg !== "..");
}

/** `/a/b` is under `/a`, `/ab` is not; a path is under itself. */
export function isUnderPath(child: string, parent: string): boolean {
  return child === parent || child.startsWith(`${parent}/`);
}

/** The allowed root containing `cwd`, or null. */
export function findWorkdirFor<T extends { path: string }>(
  cwd: string,
  workdirs: readonly T[],
): T | null {
  if (!isCanonicalAbsPath(cwd)) return null;
  return workdirs.find((w) => isUnderPath(cwd, w.path)) ?? null;
}

/** The Slack MCP tool that places a draft message. */
export const SLACK_DRAFT_TOOL = "slack_send_message_draft";

/** Points at one external tool. */
export interface ExternalToolRef {
  server: string;
  appId: string | null;
  tool: string;
}

/**
 * The Slack draft tool among the servers (on a server whose name contains
 * slack; the first one). null when none.
 */
export function slackDraftToolOf(
  servers: readonly {
    name: string;
    appId: string | null;
    tools: readonly { name: string }[];
    closed?: boolean;
  }[],
): ExternalToolRef | null {
  for (const s of servers) {
    if (s.closed === true || !s.name.toLowerCase().includes("slack")) continue;
    if (s.tools.some((t) => t.name === SLACK_DRAFT_TOOL))
      return { server: s.name, appId: s.appId, tool: SLACK_DRAFT_TOOL };
  }
  return null;
}
